import React, { memo, Fragment, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTheme, createUseStyles, Modal, Select, Advice, Text, Loader } from 'hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import { getList } from "../../lib/services/valueLists";
import Filters, { FiltersProvider } from "../Filters";
import FilterOff from "./icons/FilterOff";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'ModalFilters');
var _ref5 = /*#__PURE__*/React.createElement(Loader, {
  size: "massive"
});
function ModalFilters(_ref) {
  let {
    advice,
    adviceType = 'info',
    className,
    title,
    confirmText,
    cancelText,
    backText,
    placeholderSearch,
    isOpen,
    schema,
    selectOptions,
    onConfirm,
    onCancel = () => {},
    onBack,
    size = 'medium',
    confirmIsDisabled,
    overlayClassName,
    postComponent,
    isLoading,
    isReady,
    selectedFilters,
    openFilters,
    filtersValues,
    onChangeSelectedFilters,
    onChangeFilter,
    onToggleFilter
  } = _ref;
  const classes = useStyles();
  const theme = useTheme();
  const {
    t,
    language
  } = useWeb5Context();
  const rootClassNames = classnames(classes.root, className, {
    [classes.notReady]: !isReady
  });
  const debounce = useRef(null);
  const cachedOptions = useRef({});
  const loadFuzzyOptions = useCallback(field => {
    return text => {
      return new Promise(resolve => {
        if (!field.inputAttrs) return resolve([]);
        if (cachedOptions.current[text]) return resolve(cachedOptions[text]);
        clearTimeout(debounce.current);
        debounce.current = setTimeout(() => {
          getList({
            list: field.inputAttrs.list,
            language,
            searchText: text
          }).then(_ref2 => {
            let {
              data
            } = _ref2;
            let options = data.map(branch => ({
              value: branch.id,
              label: branch.description
            }));
            if (field.beforeRenderValueList) {
              options = field.beforeRenderValueList(options);
            }
            if (field.groupFunction) {
              options = field.groupFunction(data);
            }
            cachedOptions.current[text] = options;
            resolve(options);
          }).catch(console.error);
        }, 500);
      });
    };
  }, [language]);
  const loadOptions = useCallback(field => () => {
    var _field$inputAttrs;
    if (!(field !== null && field !== void 0 && (_field$inputAttrs = field.inputAttrs) !== null && _field$inputAttrs !== void 0 && _field$inputAttrs.list)) return Promise.resolve([]);
    return getList({
      list: field.inputAttrs.list,
      language
    }).then(_ref3 => {
      let {
        data
      } = _ref3;
      return data.map(branch => ({
        value: branch.id,
        label: branch.description
      }));
    });
  }, [language]);
  const finalSchema = useMemo(() => {
    schema === null || schema === void 0 ? void 0 : schema.map(el => {
      el.fields.map(field => {
        if (!(field !== null && field !== void 0 && field.inputProps)) field.inputProps = {};
        field.inputProps.className = `section-filter__field__${field.id}`;
        field.inputProps.classes = {
          input: `section-filter__field__${field.id}--input`,
          clear: `section-filter__field__${field.id}--clear`,
          calendarIcon: `section-filter__field__${field.id}--calendar`,
          clockIcon: `section-filter__field__${field.id}--clock`
        };
        if (field.dataType === 'multipleValueList') {
          var _field$inputAttrs2, _field$inputAttrs3, _field$inputAttrs5;
          field.inputProps.isMulti = true;
          if (field !== null && field !== void 0 && (_field$inputAttrs2 = field.inputAttrs) !== null && _field$inputAttrs2 !== void 0 && _field$inputAttrs2.list && field !== null && field !== void 0 && (_field$inputAttrs3 = field.inputAttrs) !== null && _field$inputAttrs3 !== void 0 && _field$inputAttrs3.isFuzzy) {
            var _field$inputAttrs4;
            field.inputProps.isFuzzy = true;
            field.inputProps.loadOptions = loadFuzzyOptions(field);
            field.inputProps.defaultOptions = true;
            field.inputProps.defaultSearch = ((_field$inputAttrs4 = field.inputAttrs) === null || _field$inputAttrs4 === void 0 ? void 0 : _field$inputAttrs4.defaultSearch) || '';
          } else if (field !== null && field !== void 0 && (_field$inputAttrs5 = field.inputAttrs) !== null && _field$inputAttrs5 !== void 0 && _field$inputAttrs5.list) {
            field.inputProps.loadOptions = loadOptions(field);
          }
        }
        return field;
      });
      return el;
    });
    return schema;
  }, [loadFuzzyOptions, loadOptions, schema]);
  const selectedFiltesSchema = useMemo(() => finalSchema.filter(el => el.id in filtersValues), [finalSchema, filtersValues]);
  var _ref4 = /*#__PURE__*/React.createElement(Select, {
    placeholder: placeholderSearch,
    options: selectOptions,
    onChange: onChangeSelectedFilters,
    value: selectedFilters,
    isFuzzy: true,
    isMulti: true,
    isFullWidth: true,
    hideMultivalueChips: true
  });
  const preComponent = useMemo(() => {
    if (!isReady) return null;
    return /*#__PURE__*/React.createElement(Fragment, null, advice && /*#__PURE__*/React.createElement(Advice, {
      type: adviceType,
      className: classes.advice,
      showIcon: true,
      showCollapse: false
    }, advice), /*#__PURE__*/React.createElement("div", {
      className: classes.search
    }, _ref4));
  }, [advice, adviceType, classes.advice, classes.search, isReady, onChangeSelectedFilters, placeholderSearch, selectOptions, selectedFilters]);
  const modalClasses = useMemo(() => ({
    content: classes.content,
    deleteButton: classes.backButton
  }), [classes.content, classes.backButton]);
  const overrides = {
    confirmButton: {
      isLoading,
      isDisabled: confirmIsDisabled
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    className: rootClassNames,
    overlayClassName: overlayClassName || classes.overlay,
    classes: modalClasses,
    title: title,
    isOpen: isOpen,
    size: size,
    useContentStaticHeight: true,
    onRequestClose: onCancel,
    onCancel: onCancel,
    onConfirm: onConfirm,
    onDelete: onBack,
    cancelText: cancelText,
    confirmText: confirmText,
    deleteText: backText,
    preComponent: preComponent,
    postComponent: postComponent,
    overrides: overrides
  }, !isReady && /*#__PURE__*/React.createElement("div", {
    className: classes.loader
  }, _ref5), isReady && /*#__PURE__*/React.createElement("div", {
    className: classes.optionsContainer
  }, !selectedFilters.length && /*#__PURE__*/React.createElement("div", {
    className: classes.emptyContainer
  }, /*#__PURE__*/React.createElement(FilterOff, {
    color: theme.colors.grey[500],
    className: classes.emptyIcon
  }), /*#__PURE__*/React.createElement(Text, {
    className: classes.emptyText
  }, t('cfm_label_download_data_template_no_filters')), /*#__PURE__*/React.createElement(Text, {
    className: classes.emptyText
  }, t('cfm_label_download_data_template_no_filters_2'))), !!selectedFilters.length && /*#__PURE__*/React.createElement(FiltersProvider, null, /*#__PURE__*/React.createElement(Filters, {
    className: classes.filters,
    useDrawer: false,
    schema: selectedFiltesSchema,
    toggled: openFilters,
    showAdd: false,
    onToggle: onToggleFilter,
    onChange: onChangeFilter,
    values: filtersValues,
    useViewsInFilters: false,
    useHeader: false,
    useSections: false,
    isPinnable: false,
    isClearable: false,
    isFullWidth: true
  }))));
}
process.env.NODE_ENV !== "production" ? ModalFilters.propTypes = {
  advice: PropTypes.string,
  adviceType: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  backText: PropTypes.string,
  placeholderSearch: PropTypes.string,
  isOpen: PropTypes.bool,
  schema: PropTypes.array,
  selectOptions: PropTypes.array,
  onChange: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  onBack: PropTypes.func,
  size: PropTypes.string,
  confirmIsDisabled: PropTypes.bool,
  overlayClassName: PropTypes.string,
  postComponent: PropTypes.object,
  isLoading: PropTypes.bool
} : void 0;
export default /*#__PURE__*/memo(ModalFilters);